import { useCallback, useMemo, useState } from 'react';

import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import SelectSearch from 'components/fields/SelectSearch';
import MultiSelect from 'components/fields/MultiSelect';
import {
  apiCreateMvrAdminCarrierRules,
  apiUpdateMvrAdminCarrierRule,
} from 'api';
import type {
  MVRAdminCarrierRule,
  MVRAdminCarrierRuleCreatePayload,
  MVRAdminCarrierRuleUpdatePayload,
  MVRCarrierRuleOptions,
  MVRMethod,
  MVRProductType,
  MVRRequestTransactionType,
  MVRValidationConfidence,
  MvrFormatRuleSpec,
} from 'api';

import { getErrorMessage } from 'utils/apiFieldErrors';

import MvrFormatRuleBuilder from './MvrFormatRuleBuilder';
import {
  buildRegexAndDescription,
  type MvrFormatBuildResult,
} from '../regexBuilder';

const PORTAL_LINK_PREFIX = 'https://';
const stripPortalLinkPrefix = (value: string) =>
  value.replace(/^\s*https?:\/\//i, '');

interface MvrCarrierRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  carrierId: number;
  carrierName: string;
  options: MVRCarrierRuleOptions;
  seed: MVRAdminCarrierRule | null;
  mode: 'create' | 'edit';
}

interface FormState {
  transaction_type: MVRRequestTransactionType | '';
  product_type: MVRProductType | '';
  product_types: MVRProductType[];
  provinces: string[];
  province_code: string;
  mvr_method: MVRMethod | '';
  portal_link: string;
  format_confidence: MVRValidationConfidence;
}

const buildInitialForm = (
  mode: 'create' | 'edit',
  seed: MVRAdminCarrierRule | null,
): FormState => ({
  transaction_type: seed?.transaction_type ?? '',
  product_type: seed?.product_type ?? '',
  product_types: seed?.product_type ? [seed.product_type] : [],
  provinces: [],
  province_code: mode === 'edit' ? (seed?.province_code ?? '') : '',
  mvr_method: seed?.mvr_method ?? '',
  portal_link: stripPortalLinkPrefix(seed?.portal_link ?? ''),
  format_confidence: seed?.format_confidence ?? '',
});

interface FormatState {
  spec: MvrFormatRuleSpec;
  result: MvrFormatBuildResult;
  description: string;
  incomplete: boolean;
}

const buildInitialFormatState = (
  seed: MVRAdminCarrierRule | null,
): FormatState => {
  const spec = seed?.format_rule_spec ?? {};
  return {
    spec,
    result: buildRegexAndDescription(spec),
    description: seed?.format_pattern_description ?? '',
    incomplete: false,
  };
};

const MvrCarrierRuleModal = ({
  isOpen,
  onClose,
  carrierId,
  carrierName,
  options,
  seed,
  mode,
}: MvrCarrierRuleModalProps) => {
  const toast = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState<FormState>(() =>
    buildInitialForm(mode, seed),
  );
  const updateField = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => setForm((f) => ({ ...f, [field]: value }));

  const [format, setFormat] = useState<FormatState>(() =>
    buildInitialFormatState(seed),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [nonFieldError, setNonFieldError] = useState<string | null>(null);

  const handleFormatChange = useCallback(
    (
      spec: MvrFormatRuleSpec,
      result: MvrFormatBuildResult,
      description: string,
      incomplete: boolean,
    ) => setFormat({ spec, result, description, incomplete }),
    [],
  );

  const provinceSelectOptions = useMemo(
    () => options.provinces.map((p) => ({ value: p.code, label: p.name })),
    [options.provinces],
  );
  const provinceMultiSelectItems = useMemo(
    () => options.provinces.map((p) => ({ id: p.id, name: p.name })),
    [options.provinces],
  );
  const provinceCodeById = useMemo(
    () => new Map(options.provinces.map((p) => [p.id, p.code])),
    [options.provinces],
  );
  const selectedProvinceIds = useMemo(
    () =>
      options.provinces
        .filter((p) => form.provinces.includes(p.code))
        .map((p) => p.id),
    [options.provinces, form.provinces],
  );

  const productTypeMultiSelectItems = useMemo(
    () => options.product_types.map((o, i) => ({ id: i, name: o.label })),
    [options.product_types],
  );
  const selectedProductTypeIds = useMemo(
    () =>
      options.product_types
        .map((o, i) =>
          form.product_types.includes(o.value as MVRProductType) ? i : -1,
        )
        .filter((i) => i >= 0),
    [options.product_types, form.product_types],
  );

  const isCLTransaction =
    form.transaction_type === 'CLNB' || form.transaction_type === 'CLPC';
  const isFormatDisabled = form.transaction_type === 'CLNB';
  const formatHint = isFormatDisabled
    ? "CL New Business quote or reference numbers aren't format-checked, so no format rule is saved for this transaction type."
    : !form.format_confidence
      ? 'Select a format confidence above to build a format rule.'
      : null;
  const formatError =
    errors.format_pattern_regex ||
    errors.format_rule_spec ||
    errors.format_confidence;

  const invalidateRules = () =>
    qc.invalidateQueries({ queryKey: ['mvr-admin-carrier-rules', carrierId] });
  const invalidateBrokerFacing = () =>
    qc.invalidateQueries({ queryKey: ['mvr-carriers-for-province'] });

  const handleError = (error: any) => {
    if (error?.error === 'No response' || error?.error === 'Unhandled error') {
      setErrors({});
      setNonFieldError(
        'Something went wrong saving this rule. Please try again.',
      );
      toast({ title: 'Could not save rule', status: 'error' });
      return;
    }

    const inlineFields = new Set([
      ...(mode === 'edit'
        ? ['product_type', 'province_code']
        : ['product_types']),
      'portal_link',
      'format_confidence',
      'format_pattern_regex',
      'format_rule_spec',
    ]);
    const formatted: Record<string, string> = {};
    const topLevel: string[] = [];

    Object.entries(error ?? {}).forEach(([field, messages]) => {
      const message = getErrorMessage(messages);
      if (!message) return;
      if (inlineFields.has(field)) {
        formatted[field] = message;
      } else {
        topLevel.push(message);
      }
    });

    setErrors(formatted);
    setNonFieldError(topLevel.length ? topLevel.join(' ') : null);
    toast({ title: 'Please fix the errors and try again', status: 'error' });
  };

  const createMutation = useMutation({
    mutationFn: (payload: MVRAdminCarrierRuleCreatePayload) =>
      apiCreateMvrAdminCarrierRules(carrierId, payload),
    onSuccess: () => {
      toast({ title: 'Rule created', status: 'success' });
      invalidateRules();
      invalidateBrokerFacing();
      onClose();
    },
    onError: handleError,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: MVRAdminCarrierRuleUpdatePayload) =>
      apiUpdateMvrAdminCarrierRule(carrierId, seed!.id, payload),
    onSuccess: () => {
      toast({ title: 'Rule updated', status: 'success' });
      invalidateRules();
      invalidateBrokerFacing();
      onClose();
    },
    onError: handleError,
  });

  const isBusy = createMutation.isPending || updateMutation.isPending;

  const hasRequiredFields =
    !!form.transaction_type &&
    !!form.mvr_method &&
    (form.mvr_method !== 'carrier_portal' || !!form.portal_link.trim()) &&
    (mode === 'edit' ? !!form.province_code : form.provinces.length > 0) &&
    (!isCLTransaction ||
      (mode === 'edit' ? !!form.product_type : form.product_types.length > 0));

  const canSave = hasRequiredFields && (isFormatDisabled || !format.incomplete);

  const handleSubmit = () => {
    const { transaction_type, mvr_method } = form;
    if (!canSave || !transaction_type || !mvr_method) return;

    const hasRule = !isFormatDisabled && !!format.result.regex;
    const sharedPayload = {
      transaction_type,
      mvr_method,
      portal_link:
        mvr_method === 'carrier_portal'
          ? `${PORTAL_LINK_PREFIX}${form.portal_link.trim()}`
          : '',
      format_confidence: isFormatDisabled ? '' : form.format_confidence,
      format_pattern_regex: (hasRule && format.result.regex) || '',
      format_pattern_description: hasRule ? format.description : '',
      format_rule_spec: hasRule ? format.spec : null,
    };

    if (mode === 'edit') {
      updateMutation.mutate({
        ...sharedPayload,
        product_type: isCLTransaction ? form.product_type : '',
        province_code: form.province_code,
      });
    } else {
      createMutation.mutate({
        ...sharedPayload,
        product_types: isCLTransaction ? form.product_types : [],
        provinces: form.provinces,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {mode === 'edit' ? 'Edit carrier rule' : 'Add carrier rule'}
          {carrierName && (
            <Text fontSize="sm" fontWeight="400" color="gray.500" mt={1}>
              {carrierName}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {nonFieldError && (
              <Alert status="error" fontSize="sm" borderRadius="md">
                <AlertIcon />
                {nonFieldError}
              </Alert>
            )}

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <VStack align="stretch" spacing={4}>
                <Text fontSize="sm" fontWeight="700">
                  Where it applies
                </Text>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Transaction type</FormLabel>
                  <SelectSearch
                    options={options.transaction_types}
                    value={form.transaction_type}
                    onChange={(v) => {
                      const isCL = v === 'CLNB' || v === 'CLPC';
                      setForm((f) => ({
                        ...f,
                        transaction_type:
                          (v as MVRRequestTransactionType) || '',
                        product_type: isCL ? f.product_type : '',
                        product_types: isCL ? f.product_types : [],
                      }));
                    }}
                    placeholder="Select transaction type"
                  />
                </FormControl>

                {isCLTransaction && mode === 'create' && (
                  <FormControl isRequired isInvalid={!!errors.product_types}>
                    <FormLabel fontSize="sm">Product types</FormLabel>
                    <MultiSelect
                      selectedIds={selectedProductTypeIds}
                      availableItems={productTypeMultiSelectItems}
                      isLoading={false}
                      itemLabelSingular="product type"
                      itemLabelPlural="product types"
                      onChange={(ids) =>
                        updateField(
                          'product_types',
                          ids
                            .map((id) => options.product_types[id]?.value)
                            .filter((v): v is MVRProductType => !!v),
                        )
                      }
                    />
                    <FormErrorMessage>{errors.product_types}</FormErrorMessage>
                  </FormControl>
                )}

                {isCLTransaction && mode === 'edit' && (
                  <FormControl isRequired isInvalid={!!errors.product_type}>
                    <FormLabel fontSize="sm">Product type</FormLabel>
                    <SelectSearch
                      options={options.product_types}
                      value={form.product_type}
                      onChange={(v) =>
                        updateField('product_type', (v as MVRProductType) || '')
                      }
                      placeholder="Select product type"
                      isInvalid={!!errors.product_type}
                    />
                    <FormErrorMessage>{errors.product_type}</FormErrorMessage>
                  </FormControl>
                )}

                {mode === 'edit' ? (
                  <FormControl isRequired isInvalid={!!errors.province_code}>
                    <FormLabel fontSize="sm">Province</FormLabel>
                    <SelectSearch
                      options={provinceSelectOptions}
                      value={form.province_code}
                      onChange={(v) =>
                        updateField('province_code', (v as string) || '')
                      }
                      placeholder="Select province"
                      isInvalid={!!errors.province_code}
                    />
                    <FormErrorMessage>{errors.province_code}</FormErrorMessage>
                  </FormControl>
                ) : (
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Provinces</FormLabel>
                    <MultiSelect
                      selectedIds={selectedProvinceIds}
                      availableItems={provinceMultiSelectItems}
                      isLoading={false}
                      itemLabelSingular="province"
                      itemLabelPlural="provinces"
                      onChange={(ids) =>
                        updateField(
                          'provinces',
                          ids
                            .map((id) => provinceCodeById.get(id))
                            .filter((code): code is string => !!code),
                        )
                      }
                    />
                  </FormControl>
                )}

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Method</FormLabel>
                  <SelectSearch
                    options={options.mvr_methods}
                    value={form.mvr_method}
                    onChange={(v) =>
                      updateField('mvr_method', (v as MVRMethod) || '')
                    }
                    placeholder="Select method"
                  />
                </FormControl>

                {form.mvr_method === 'carrier_portal' && (
                  <FormControl isRequired isInvalid={!!errors.portal_link}>
                    <FormLabel fontSize="sm">Portal link</FormLabel>
                    <InputGroup>
                      <InputLeftAddon borderLeftRadius="16px" fontSize="sm">
                        {PORTAL_LINK_PREFIX}
                      </InputLeftAddon>
                      <Input
                        variant="main"
                        value={form.portal_link}
                        placeholder="carrier-portal.com/login"
                        onChange={(e) =>
                          updateField(
                            'portal_link',
                            stripPortalLinkPrefix(e.target.value),
                          )
                        }
                      />
                    </InputGroup>
                    <FormErrorMessage>{errors.portal_link}</FormErrorMessage>
                  </FormControl>
                )}
              </VStack>

              <VStack align="stretch" spacing={4}>
                <Text fontSize="sm" fontWeight="700">
                  Policy number format
                </Text>

                <FormControl isInvalid={!!errors.format_confidence}>
                  <FormLabel fontSize="sm">Format confidence</FormLabel>
                  <SelectSearch
                    options={options.format_confidences}
                    value={isFormatDisabled ? '' : form.format_confidence}
                    isDisabled={isFormatDisabled}
                    onChange={(v) => {
                      const nextConfidence =
                        (v as MVRValidationConfidence) || '';
                      updateField('format_confidence', nextConfidence);
                      if (!nextConfidence)
                        setFormat(buildInitialFormatState(null));
                    }}
                    placeholder="No format check"
                    allowClear
                    isInvalid={!!errors.format_confidence}
                  />
                </FormControl>

                {formatHint ? (
                  <Text fontSize="sm" color="gray.500">
                    {formatHint}
                  </Text>
                ) : (
                  <MvrFormatRuleBuilder
                    initialValue={format.spec}
                    initialDescription={format.description}
                    onChange={handleFormatChange}
                  />
                )}

                {formatError && (
                  <Alert status="error" fontSize="sm" borderRadius="md">
                    <AlertIcon />
                    {formatError}
                  </Alert>
                )}
              </VStack>
            </SimpleGrid>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="brand"
            isLoading={isBusy}
            isDisabled={!canSave}
            onClick={handleSubmit}
          >
            {mode === 'edit' ? 'Save changes' : 'Create rule'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MvrCarrierRuleModal;
